import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  BookOpen, Award, TrendingUp, Clock, CalendarClock, ArrowRight,
} from 'lucide-react';
import Modal from './Modal';
import { analyticsAPI } from '../../services/api';
import { useAppContext } from '../../context/AppContext';
import './ModuleSummaryModal.css';

/**
 * Compact trainee analytics preview shown when clicking a trainee row.
 * `traineeRef` accepts a numeric id, dashless UUID, or dashed UUID.
 */
const TraineeSummaryModal = ({ isOpen, onClose, traineeRef }) => {
  const navigate = useNavigate();
  const { showNotification } = useAppContext();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!isOpen || !traineeRef) {
      setData(null);
      setError(null);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    analyticsAPI.getTraineeAnalytics(traineeRef)
      .then((response) => {
        if (cancelled) return;
        if (response.data.success) {
          setData(response.data.data);
        } else {
          setError('Failed to load trainee analytics');
        }
      })
      .catch((err) => {
        if (cancelled) return;
        const msg = err.response?.data?.error || 'Failed to load trainee analytics';
        setError(msg);
        showNotification(msg, 'error');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, [isOpen, traineeRef, showNotification]);

  const dashless = (uuid) => (typeof uuid === 'string' ? uuid.replace(/-/g, '') : null);

  const goToFullPage = () => {
    if (!data) return;
    const t = data.trainee;
    onClose();
    if (t.username) {
      navigate(`/trainees/${t.username}`);
    } else {
      const ref = dashless(t.userUuid) || t.id;
      navigate(`/analytics/trainee/${ref}`);
    }
  };

  const renderBody = () => {
    if (loading) {
      return <p style={{ textAlign: 'center', padding: 'var(--space-5)', color: 'var(--text-secondary)' }}>Loading…</p>;
    }
    if (error || !data) {
      return <p style={{ textAlign: 'center', padding: 'var(--space-5)', color: 'var(--text-secondary)' }}>{error || 'No data available.'}</p>;
    }

    const t = data.trainee;
    const topModules = (data.moduleProgress || [])
      .slice()
      .sort((a, b) => (b.progress || 0) - (a.progress || 0))
      .slice(0, 5);

    return (
      <div className="module-summary">
        <div className="module-summary__meta">
          {t.department && <span className="badge badge-dark">{t.department}</span>}
          <span className="badge badge-gray" style={{ marginLeft: 6 }}>{t.role}</span>
          {t.status && (
            <span className={`badge ${t.status === 'Active' ? 'badge-green' : 'badge-gray'}`} style={{ marginLeft: 6 }}>
              {t.status}
            </span>
          )}
          {t.email && <span style={{ marginLeft: 8, color: 'var(--text-secondary)' }}>· {t.email}</span>}
        </div>

        <div className="module-summary__stats">
          {[
            { icon: BookOpen, label: 'Enrolled', value: data.totalModules, sub: `${data.completedModules} completed` },
            { icon: TrendingUp, label: 'Overall Progress', value: `${data.overallProgress}%`, sub: data.contentProgress != null ? `Content ${data.contentProgress}%` : null },
            { icon: Clock, label: 'Time Spent', value: `${data.totalTimeSpentHours}h`, sub: null },
            {
              icon: CalendarClock,
              label: 'Last Activity',
              value: data.stats?.lastActivity ? new Date(data.stats.lastActivity).toLocaleDateString() : '—',
              sub: data.stats?.lastActivity ? new Date(data.stats.lastActivity).toLocaleTimeString() : null,
            },
          ].map(({ icon: Icon, label, value, sub }) => (
            <div key={label} className="module-summary__stat">
              <Icon size={16} className="module-summary__stat-icon" />
              <div className="module-summary__stat-body">
                <span className="module-summary__stat-value">{value}</span>
                <span className="module-summary__stat-label">{label}</span>
                {sub && <span className="module-summary__stat-sub">{sub}</span>}
              </div>
            </div>
          ))}
        </div>

        {topModules.length > 0 && (
          <div className="module-summary__top">
            <div className="module-summary__top-header">
              <span>Top modules by progress</span>
              <span className="badge badge-dark">{data.moduleProgress.length} enrolled</span>
            </div>
            {topModules.map((m, idx) => (
              <div key={m.moduleId} className="module-summary__trainee">
                <span className="module-summary__rank">#{idx + 1}</span>
                <span className="module-summary__trainee-name">{m.title}</span>
                <div className="progress-bar" style={{ height: 6, flex: 1 }}>
                  <div className="progress-bar-fill" style={{ width: `${m.progress}%` }} />
                </div>
                <span className="module-summary__trainee-pct">
                  {m.completed ? <Award size={12} style={{ marginRight: 4, verticalAlign: 'middle' }} /> : null}
                  {m.progress}%
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="lg"
      title={data?.trainee?.name || 'Trainee Analytics'}
      footer={
        <>
          <button className="btn btn-outline" onClick={onClose}>Close</button>
          <button
            className="btn btn-primary"
            onClick={goToFullPage}
            disabled={!data}
          >
            View Full Analytics <ArrowRight size={14} style={{ marginLeft: 4 }} />
          </button>
        </>
      }
    >
      {renderBody()}
    </Modal>
  );
};

export default TraineeSummaryModal;
